// Calendar event (appointment) title rendering. Mirrors the backend
// decoda/services/event_title.py so the calendar, appointment form, and the
// settings preview all produce identical titles.
//
// An empty stored title means "render dynamically" from the tenant's format; a
// non-empty title (manual/CUSTOM, imported, or legacy) is used verbatim
// (COALESCE semantics). The services half is scoped per provider when an
// appointment is rendered on a single provider's calendar lane.
import { formatName } from "@/lib/format-data";
import { toMs } from "@/lib/timestamp";

import { UserType } from "@/types/enums";
import { EventType } from "@/types/enums/event";
import { EventTitleFormat } from "@/types/enums/event-title-format";
import { EventTiny, getServiceSegments } from "@/types/event";

const SERVICE_JOINER = " + ";
const PART_SEPARATOR = " - ";
const DEFAULT_TITLE = "Appointment";

export interface EventTitleParts {
  patientFull: string;
  patientFirst: string;
  patientInitials: string;
  patientPreferredFull: string;
  serviceNames: string[];
}

export interface PatientNameLike {
  firstName: string;
  lastName?: string | null;
  // Never normalized on write (patients edit it in the portal, importers copy it
  // verbatim), so it is trimmed before use.
  preferredName?: string | null;
}

function joinServices(serviceNames: string[]): string {
  // Drop empty service names; keep real ones verbatim (including a service that
  // happens to be named "Appointment").
  return serviceNames.filter((n) => !!n).join(SERVICE_JOINER);
}

// Compose a patient + services title, matching the legacy generator's
// fallbacks: a lone patient gets an "Appointment" suffix, a lone service does
// not, and an empty appointment falls back to "Appointment".
function patientServices(
  patientLabel: string,
  services: string,
  servicesFirst: boolean,
): string {
  if (!patientLabel && !services) return DEFAULT_TITLE;
  if (patientLabel && !services) return `${patientLabel} ${DEFAULT_TITLE}`;
  if (services && !patientLabel) return services;
  return servicesFirst
    ? `${services}${PART_SEPARATOR}${patientLabel}`
    : `${patientLabel}${PART_SEPARATOR}${services}`;
}

export function patientNameParts(patient?: PatientNameLike): {
  patientFull: string;
  patientFirst: string;
  patientInitials: string;
  patientPreferredFull: string;
} {
  if (!patient || !patient.firstName) {
    return {
      patientFull: "",
      patientFirst: "",
      patientInitials: "",
      patientPreferredFull: "",
    };
  }
  const patientFull = formatName(patient.firstName, patient.lastName);
  const patientFirst = formatName(patient.firstName, undefined);
  const patientInitials = [patient.firstName, patient.lastName]
    .filter((p): p is string => !!p)
    .map((p) => p[0].toUpperCase())
    .join("");
  // The preferred name replaces the first name only; the last name stays legal.
  const preferred = (patient.preferredName ?? "").trim();
  const patientPreferredFull = formatName(
    preferred || patient.firstName,
    patient.lastName,
  );
  return { patientFull, patientFirst, patientInitials, patientPreferredFull };
}

export function renderEventTitle(
  format: EventTitleFormat,
  parts: EventTitleParts,
): string {
  const services = joinServices(parts.serviceNames);
  switch (format) {
    case EventTitleFormat.SERVICES_ONLY:
      return services || DEFAULT_TITLE;
    case EventTitleFormat.PATIENT_ONLY:
      return parts.patientFull || DEFAULT_TITLE;
    case EventTitleFormat.SERVICES_PATIENT:
      return patientServices(parts.patientFull, services, true);
    case EventTitleFormat.INITIALS_SERVICES:
      return patientServices(parts.patientInitials, services, false);
    case EventTitleFormat.FIRST_NAME_SERVICES:
      return patientServices(parts.patientFirst, services, false);
    case EventTitleFormat.PREFERRED_NAME_SERVICES:
      // Degrade to the legal name so this format is never worse than
      // PATIENT_SERVICES, whatever the caller was able to supply.
      return patientServices(
        parts.patientPreferredFull || parts.patientFull,
        services,
        false,
      );
    // PATIENT_SERVICES (the default) and CUSTOM (last-resort default).
    case EventTitleFormat.PATIENT_SERVICES:
    case EventTitleFormat.CUSTOM:
      return patientServices(parts.patientFull, services, false);
    default: {
      // Compile-time guard: if EventTitleFormat grows, this branch becomes a type
      // error instead of silently rendering the new format as PATIENT_SERVICES.
      // Runtime guard: an unrecognized value still renders the default format.
      const _exhaustive: never = format;
      void _exhaustive;
      return patientServices(parts.patientFull, services, false);
    }
  }
}

// Render a title for a patient + selected service ids. Used by the appointment
// form's auto-title and the settings preview.
export function renderTitleFromServiceIds(
  format: EventTitleFormat,
  patient: PatientNameLike | undefined,
  serviceIds: string[],
  services: { id: string; name: string }[],
): string {
  const parts = patientNameParts(patient);
  const serviceNames = serviceIds
    .map((id) => services.find((s) => s.id === id)?.name)
    .filter((n): n is string => !!n);
  return renderEventTitle(format, { ...parts, serviceNames });
}

// Title to display for an event: a non-empty stored title wins (COALESCE),
// otherwise render from the tenant format. `serviceNames` should already be
// scoped to the provider's lane when rendering a single provider's column.
export function resolveEventTitle(params: {
  storedTitle?: string | null;
  format: EventTitleFormat;
  patient?: PatientNameLike;
  serviceNames: string[];
}): string {
  // A whitespace-only title would render blank, so fall back to a dynamic title.
  if (params.storedTitle && params.storedTitle.trim())
    return params.storedTitle;
  // CUSTOM is manual-only: a blank/cleared title stays blank, never the preset
  // patient/service renderer.
  if (params.format === EventTitleFormat.CUSTOM) return "";
  const parts = patientNameParts(params.patient);
  return renderEventTitle(params.format, {
    ...parts,
    serviceNames: params.serviceNames,
  });
}

// Resolve the title to display for a calendar event. A stored title wins
// (COALESCE); otherwise it is rendered from the tenant format. Pass a
// `providerId` to scope the services to a single provider's calendar lane;
// omit it for a combined title (tooltips, search, detail). Prefer the
// `useEventTitle` hook in components — it supplies the tenant's format.
export function resolveCalendarEventTitle(
  event: EventTiny,
  format: EventTitleFormat,
  providerId?: string,
): string {
  // The dynamic format is appointment-only; blocks/shifts/gcal keep their title.
  if (event.type !== EventType.APPOINTMENT) {
    return event.title ?? "";
  }
  // Exclude archived (removed) services so they don't linger in the title.
  const services = getServiceSegments(event.segments || [])
    .filter((s) => !s.isArchived)
    .sort((a, b) => {
      const aStart = toMs(a.start ?? event.start);
      const bStart = toMs(b.start ?? event.start);
      return aStart - bStart;
    });
  const scoped = providerId
    ? services.filter((s) => s.providerId === providerId)
    : services;
  const laneServices = scoped.length ? scoped : services;
  // Add-ons read as part of their parent service, so the title names the parent only.
  const parents = laneServices.filter((s) => !s.serviceAddonId);
  const source = parents.length ? parents : laneServices;
  const serviceNames = source
    .map((s) => s.service?.name)
    .filter((n): n is string => !!n);
  const patient = event.attendees?.find(
    (a) => a.user.type === UserType.PATIENT,
  )?.user;
  return resolveEventTitle({
    storedTitle: event.title,
    format,
    patient,
    serviceNames,
  });
}
